import { ClockAlertIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useNow } from '@/shared/components/date';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';

import type { GoalNode, GoalPriority, GoalStatus } from '../api';
import { deadlineState, type DeadlineState } from '../model/filters';
import { PRIORITY_STRIPE, STATUS_META, useDeadlineLabel } from '../model/visuals';

export function PriorityDot({ priority, className }: { priority: GoalPriority; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-2.5 shrink-0 rounded-full', PRIORITY_STRIPE[priority], className)}
    />
  );
}

export function PriorityLabel({ priority, className }: { priority: GoalPriority; className?: string }) {
  const { t } = useTranslation('goals');
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} data-priority={priority}>
      <PriorityDot priority={priority} />
      {t(`priority.${priority}`)}
    </span>
  );
}

export function StatusBadge({ status, className }: { status: GoalStatus; className?: string }) {
  const { t } = useTranslation('goals');
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <Badge variant={meta.badge} className={cn('gap-1', className)} data-status={status}>
      <Icon aria-hidden />
      {t(`status.${status}`)}
    </Badge>
  );
}

/** A thin progress bar with its percentage; green at 100 %. */
export function ProgressBar({
  value,
  className,
  showValue = true,
  size = 'sm',
}: {
  value: number;
  className?: string;
  showValue?: boolean;
  size?: 'xs' | 'sm';
}) {
  const { t } = useTranslation('goals');
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <span className={cn('flex min-w-0 items-center gap-2', className)}>
      <span
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-label={t('fields.progress')}
        className={cn(
          'relative min-w-0 flex-1 overflow-hidden rounded-full bg-muted',
          size === 'xs' ? 'h-1' : 'h-1.5',
        )}
      >
        <span
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-[width] duration-300',
            clamped >= 100 ? 'bg-success' : 'bg-brand',
          )}
          style={{ width: `${clamped}%` }}
        />
      </span>
      {showValue && (
        <span className="shrink-0 text-[0.6875rem] font-medium text-muted-foreground tabular-nums">
          {clamped}%
        </span>
      )}
    </span>
  );
}

const DEADLINE_TONE: Record<DeadlineState, string> = {
  overdue: 'text-destructive-text',
  soon: 'text-warning-text',
  later: 'text-muted-foreground',
  none: 'text-subtle-foreground',
  closed: 'text-subtle-foreground',
};

/** The deadline with its urgency: red clock when overdue, amber within three days. */
export function DeadlineChip({
  goal,
  compact = false,
  className,
}: {
  goal: Pick<GoalNode, 'deadline' | 'deadlineHasTime' | 'status'>;
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('goals');
  const label = useDeadlineLabel();
  const now = useNow();
  if (!goal.deadline)
    return compact ? null : (
      <span className={cn('text-subtle-foreground', className)}>{t('deadline.none')}</span>
    );
  const state = deadlineState(goal, now);
  const Icon = state === 'overdue' ? ClockAlertIcon : ClockIcon;
  const full = label(goal.deadline, goal.deadlineHasTime);
  const text = compact ? label(goal.deadline, goal.deadlineHasTime, true) : full;
  return (
    <span
      className={cn('inline-flex items-center gap-1 tabular-nums', DEADLINE_TONE[state], className)}
      data-deadline={state}
      title={
        state === 'overdue'
          ? t('deadline.overdue', { date: full })
          : state === 'soon'
            ? t('deadline.soon', { date: full })
            : full
      }
    >
      <Icon className={compact ? 'size-3' : 'size-3.5'} aria-hidden />
      <span className="truncate">{text}</span>
      {!compact && state === 'overdue' && <span className="font-medium">· {t('deadline.overdueShort')}</span>}
    </span>
  );
}

export function RoleBadge({
  role,
  className,
}: {
  role: 'owner' | 'editor' | 'viewer' | 'none';
  className?: string;
}) {
  const { t } = useTranslation('goals');
  if (role === 'none') return null;
  return (
    <Badge
      variant={role === 'owner' ? 'soft' : role === 'editor' ? 'info' : 'secondary'}
      className={className}
    >
      {t(`roles.${role}`)}
    </Badge>
  );
}
